import React from 'react';
import { styled } from '@compiled/react';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { inWeekOrder, inMonthOrder } from '../messages';

type APIProps = {
	isHighlightToday: boolean;
	weekNumbers: {
		day: number;
		month: number;
	}[];
	currentMonth: number;
	columnIndex: number;
	todayCoordinates: [number, number] | undefined;
	yearCaption: string | undefined | null;
};

type Props = APIProps;

function WeekCell(props: Props) {
	const { columnIndex, weekNumbers, todayCoordinates, yearCaption, isHighlightToday } = props;
	const { formatMessage } = useIntl();

	// For the weeks granularity, the 4th day of the week determines the month that week falls in
	const monthNumber = weekNumbers[3].month;

	const todayIndex = weekNumbers.findIndex(
		(element, i) =>
			isHighlightToday &&
			todayCoordinates &&
			todayCoordinates[0] === columnIndex &&
			todayCoordinates[1] === i,
	);

	const isCurrentWeek = todayIndex >= 0;

	return (
		<>
			<Header>
				<Heading as="span" level="h100">
					<StyledHeader currentWeek={isCurrentWeek}>
						{formatMessage(inMonthOrder[monthNumber])} {yearCaption}
					</StyledHeader>
				</Heading>
			</Header>
			<DaysContainer todayIndex={todayIndex}>
				{weekNumbers.map(({ day }, dayIndex) => (
					<Day
						isHighlighted={dayIndex === todayIndex}
						key={dayIndex}
					>{`${formatMessage(inWeekOrder[dayIndex])} ${day}`}</Day>
				))}
			</DaysContainer>
		</>
	);
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Header = styled.span({
	marginTop: token('space.050', '4px'),
});
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledHeader = styled.span<{ currentWeek: boolean }>({
	textTransform: 'uppercase',
	marginTop: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ currentWeek }) =>
		currentWeek
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`${token('color.text.information', colors.B200)} !important;`
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`${token('color.text.subtlest', colors.N300)}`,
});

// Each day is 1/7th of a Week Cell in width
const dayWidth = (1 / 7) * 100;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Day = styled.span<{ isHighlighted: boolean }>({
	flex: '0 0 auto',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${dayWidth}%`,
	font: token('font.body.UNSAFE_small'),
	textAlign: 'center',
	overflowWrap: 'break-word',
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	color: ({ isHighlighted }) =>
		isHighlighted
			? // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`${token('color.text.information', colors.B200)} !important`
			: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				`${token('color.text.subtlest', colors.N300)}`,
	paddingLeft: token('space.150', '12px'),
	paddingRight: token('space.150', '12px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DaysContainer = styled.div<{ todayIndex: number }>({
	flex: '1 1 auto',
	display: 'flex',
	alignItems: 'center',
	width: '100%',
});

WeekCell.defaultProps = {
	yearCaption: null,
};

export default WeekCell;
export type { APIProps };
